import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import './ProgressBar.css';
import { throttle } from 'lodash';

const ProgressBar = ({ sections }) => {
  const [sectionVisibility, setSectionVisibility] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const progressTrackRef = useRef(null);
  const [sectionTitleVisibility, setSectionTitleVisibility] = useState({});

  // Calculate section visibility and progress
  const calculateProgress = useCallback(() => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    
    const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
    setScrollProgress(scrollPercentage);

    const visibilityData = {};
    const titleVisibility = {};
    
    sections.forEach((section, index) => {
      const element = document.getElementById(section.id);
      if (element) {
        const rect = element.getBoundingClientRect();
        const isCentered = rect.top <= windowHeight/3 && rect.bottom >= windowHeight/3;
        
        // Check if the section is coming into view (for title fade-in)
        const isVisible = rect.top <= windowHeight * 0.8;
        titleVisibility[section.id] = isVisible;
        
        visibilityData[section.id] = {
          isCentered,
          checkpointPosition: index === 0 ? 0 : 
                            index === sections.length - 1 ? 100 : 
                            (index * (100 / (sections.length - 1)))
        };
        
        if (isCentered) {
          setActiveSection(section.id);
        }
      }
    });
    
    setSectionTitleVisibility(titleVisibility);
    setSectionVisibility(visibilityData);
  }, [sections]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = throttle(() => {
      calculateProgress();
    }, 100);

    window.addEventListener('scroll', handleScroll);
    calculateProgress(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, [calculateProgress]);

  // Animation variants for the labels
  const labelVariants = {
    hidden: { 
      opacity: 0, 
      x: -10,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    visible: (custom) => ({
      opacity: custom.isActive ? 1 : (custom.isHovered ? 0.6 : 0),
      x: 0,
      scale: custom.isActive ? 1 : 0.9,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    })
  };

  return (
    <div className="progress-container">
      <div className="progress-bar-wrapper">
        <div 
          ref={progressTrackRef}
          className="progress-track"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div 
            className="progress-line"
            style={{ 
              transform: `scaleY(${scrollProgress / 100})`,
              transformOrigin: 'top'
            }}
          />
          
          {sections.map((section, index) => {
            const sectionData = sectionVisibility[section.id] || {
              isCentered: false,
              checkpointPosition: 0
            };
            
            const checkpointPosition = index === 0 ? 0 : 
                                     index === sections.length - 1 ? 100 : 
                                     (index * (100 / (sections.length - 1)));
            
            const isActive = section.id === activeSection;
            
            return (
              <motion.div
                key={section.id}
                className={`checkpoint 
                  ${sectionData.isCentered ? 'centered' : ''} 
                  ${scrollProgress >= checkpointPosition ? 'passed' : ''}`
                }
                style={{ 
                  top: `${checkpointPosition}%`,
                  background: scrollProgress >= checkpointPosition ? '#41eaff' : 'rgba(65, 234, 255, 0.2)',
                  transition: 'width 0.3s, height 0.3s, background 0.3s, opacity 0.3s, visibility 0.3s',
                  // Keep checkpoints visible but adjust opacity based on scroll
                  opacity: section.id === 'intro' ? 1 : (sectionTitleVisibility[section.id] ? 1 : 0.3),
                  visibility: 'visible',
                  display: 'block'
                }}
              >
                <motion.span 
                  className="checkpoint-label"
                  custom={{ isActive, isHovered }}
                  initial="hidden"
                  animate="visible"
                  variants={labelVariants}
                  style={{ 
                    pointerEvents: 'none',
                    fontSize: isActive ? '14px' : '12px',
                    fontWeight: isActive ? '600' : '400',
                    opacity: isActive ? 1 : (isHovered ? 0.6 : 0)
                  }}
                >
                  {section.label}
                </motion.span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
